import { useState, useRef, useEffect } from 'react';
import { Spinner } from '@/components/spinner';
import { useTranslation } from 'react-i18next';

import styles from './TeamSelector.module.css';

interface TeamSelectorProps {
  teams: Api.Team[];
  currentTeamId?: string;
  currentTeamName?: string;
  onChange: (teamId: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export function TeamSelector({
  teams,
  currentTeamId,
  currentTeamName,
  onChange,
  disabled = false,
  isLoading = false
}: TeamSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Sort teams alphabetically
  const sortedTeams = [...teams].sort((a, b) => a.name.localeCompare(b.name));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTeamSelect = (teamId: string) => {
    if (teamId !== currentTeamId) {
      onChange(teamId);
    }
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!disabled && !isLoading) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={styles.teamSelector} ref={dropdownRef}>
      <button
        type="button"
        className={`${styles.teamSelectorButton} ${isOpen ? styles.open : ''}`}
        onClick={toggleDropdown}
        disabled={disabled || isLoading}
        data-testid="teamSelectorButton"
      >
        <span className={styles.teamSelectorButtonText}>
          {currentTeamName || t('teamHeader.select')}
        </span>
        <div className={styles.teamSelectorButtonIcon}>
          {isLoading ? (
            <Spinner />
          ) : (
            <div className={`${styles.arrow} ${isOpen ? styles.arrowUp : styles.arrowDown}`} />
          )}
        </div>
      </button>

      {isOpen && (
        <div className={styles.teamSelectorDropdown} data-testid="teamSelectorDropdown">
          <div className={styles.teamSelectorHeader}>{t('teamSelector.selectCurrentTeam')}</div>
          <div className={styles.teamSelectorList}>
            {sortedTeams.map((team) => (
              <button
                key={team.team_id}
                type="button"
                className={`${styles.teamSelectorItem} ${
                  team.team_id === currentTeamId ? styles.selected : ''
                }`}
                onClick={() => handleTeamSelect(team.team_id)}
                data-testid={`teamSelectorItem-${team.team_id}`}
              >
                <div className={styles.teamSelectorItemCheckbox}>
                  {team.team_id === currentTeamId && <span className={styles.checkIcon}>✔</span>}
                </div>
                <span className={styles.teamSelectorItemText}>{team.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
